<template>
    <v-container class="main-container pa-0">
        <v-card class="header-card mb-6" flat>
            <v-card-text class="pa-6">
                <div class="text-center">
                    <h1 class="top-heading primary--text font-weight-bold">CalcuttaRecords</h1>
                    <div class="subtitle-1 grey--text text--darken-1">Inventory Management Dashboard</div>
                </div>
            </v-card-text>
        </v-card>
        <div v-if="user.role == 'manager' || user.role == 'agent'">
            <v-row class="mb-6">
                <v-col cols="12" md="4">
                    <v-card elevation="0" class="stat-card gradient-primary">
                        <v-card-text class="pa-6">
                            <div class="d-flex align-center">
                                <v-icon size="42" class="white--text mr-4">mdi-database</v-icon>
                                <div>
                                    <div class="stat-value white--text">{{ totalCount }}</div>
                                    <div class="stat-label white--text text--lighten-3">Total Listings</div>
                                </div>
                            </div>
                        </v-card-text>
                    </v-card>
                </v-col>
                <v-col cols="12" md="4">
                    <v-card elevation="0" class="stat-card gradient-amber">
                        <v-card-text class="pa-6">
                            <div class="d-flex align-center">
                                <v-icon size="42" class="white--text mr-4">mdi-clock-outline</v-icon>
                                <div>
                                    <div class="stat-value white--text">{{ pendingCount }}</div>
                                    <div class="stat-label white--text text--lighten-3">Unpublished</div>
                                </div>
                            </div>
                        </v-card-text>
                    </v-card>
                </v-col>
                <v-col cols="12" md="4">
                    <v-card elevation="0" class="stat-card gradient-success">
                        <v-card-text class="pa-6">
                            <div class="d-flex align-center">
                                <v-icon size="42" class="white--text mr-4">mdi-check-circle-outline</v-icon>
                                <div>
                                    <div class="stat-value white--text">{{ publishedCount }}</div>
                                    <div class="stat-label white--text text--lighten-3">Published</div>
                                </div>
                            </div>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>

            <v-card class="mb-6 action-card" flat>
                <v-card-text class="pa-4">
                    <v-row>
                        <v-col cols="12" lg="4" md="6" sm="12" class="d-flex align-center" order="1" order-md="0">
                            <v-btn color="primary" class="mr-3 action-btn" elevation="0"
                                @click="$router.push('/start')">
                                <v-icon left>mdi-plus</v-icon>
                                New Listing
                            </v-btn>
                            <v-btn color="secondary" class="action-btn" elevation="0" @click="$router.push('/other')">
                                <v-icon left>mdi-format-list-bulleted</v-icon>
                                Other Listing
                            </v-btn>
                        </v-col>
                        <v-col cols="12" lg="8" md="6" sm="12" class="d-flex align-center flex-wrap" order="0"
                            order-md="1">
                            <v-text-field class="mr-3 search-query" v-model="sku" label="Enter SKU" outlined dense
                                hide-details background-color="grey lighten-4"
                                prepend-inner-icon="mdi-barcode-scan"></v-text-field>
                            <v-btn color="info" class="action-btn" elevation="0" @click="openSku">
                                <v-icon left>mdi-magnify</v-icon>
                                Check & List
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>

            <v-row>

                <v-col>
                    <v-card elevation="0" class="mb-4 data-table-card">
                        <v-card-title
                            class="py-6 px-6 d-flex justify-space-between align-center flex-wrap grey lighten-4">
                            <div>
                                <span class="text-h6 font-weight-bold">Inventory Listings</span>
                                <div class="caption grey--text text--darken-1 mt-1">Manage your product catalog</div>
                            </div>
                            <v-text-field v-model="searchQuery" label="Search by SKU or Name" prepend-icon="mdi-magnify"
                                outlined dense hide-details background-color="white" class="search-bar"
                                clearable></v-text-field>
                        </v-card-title>

                        <v-data-table :headers="headers" :items="listings" :items-per-page.sync="itemsPerPage"
                            :page.sync="page" :server-items-length="totalListings" :loading="loading"
                            loading-text="Loading records..." no-data-text="No listings available"
                            class="elevation-0 table-rounded" :footer-props="{
                                'items-per-page-options': [5, 10, 15, 20, 25, 50]
                            }" hide-default-footer>
                            <template v-slot:item.title="{ item }">
                                <div class="d-flex align-center">
                                    <span class="product-title font-weight-medium" @click="rowClicked(item)">
                                        {{ item.title }}
                                    </span>
                                    <v-tooltip bottom v-if="item.sku">
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-icon small color="grey lighten-1" class="ml-2" v-bind="attrs"
                                                v-on="on">mdi-information-outline</v-icon>
                                        </template>
                                        <span>SKU: {{ item.sku }}</span>
                                    </v-tooltip>
                                </div>
                            </template>

                            <template v-slot:item.storageLocation="{ item }">
                                <v-menu offset-y left nudge-bottom="4">
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-chip
                                            x-small
                                            label
                                            v-bind="attrs"
                                            v-on="on"
                                            class="storage-loc-chip ma-0"
                                            :class="storageLocationChipClass(item)"
                                            :disabled="locationSavingId === item._id"
                                            @click.stop
                                        >
                                            <v-progress-circular
                                                v-if="locationSavingId === item._id"
                                                indeterminate
                                                size="12"
                                                width="2"
                                                class="mr-1"
                                            />
                                            <v-icon v-else x-small class="mr-1">
                                                {{ storageLocationIcon(item) }}
                                            </v-icon>
                                            {{ storageLocationShort(item) }}
                                        </v-chip>
                                    </template>
                                    <v-list dense class="storage-loc-menu py-1">
                                        <v-list-item
                                            v-for="opt in storageLocationOptions"
                                            :key="opt.value"
                                            :disabled="locationSavingId === item._id"
                                            @click="saveStorageLocation(item, opt.value)"
                                        >
                                            <v-list-item-icon class="mr-2 my-1">
                                                <v-icon small>{{ opt.icon }}</v-icon>
                                            </v-list-item-icon>
                                            <v-list-item-title class="text-body-2">
                                                {{ opt.label }}
                                            </v-list-item-title>
                                            <v-list-item-action v-if="normalizedStorageLocation(item) === opt.value">
                                                <v-icon small color="primary">mdi-check</v-icon>
                                            </v-list-item-action>
                                        </v-list-item>
                                    </v-list>
                                </v-menu>
                            </template>

                            <template v-slot:item.listPrice="{ item }">
                                <span class="font-weight-medium">
                                    <v-chip small outlined color="primary" class="price-chip">
                                        {{ item.listPrice }}
                                    </v-chip>
                                </span>
                            </template>

                            <template v-slot:item.images="{ item }">
                                <div class="image-container">
                                    <v-avatar v-if="item.images && item.images.length" size="50" class="elevation-1">
                                        <v-img :src="getImageUrl(item.images[0])"
                                            :alt="item.title"></v-img>
                                    </v-avatar>
                                    <v-avatar v-else size="50" color="grey lighten-4" class="elevation-1">
                                        <v-icon color="grey darken-2">mdi-image-off</v-icon>
                                    </v-avatar>
                                </div>
                            </template>

                            <template v-slot:item.published="{ item }">
                                <div class="status-container">
                                    <v-chip v-if="item.published" color="success" text-color="white" small pill
                                        class="font-weight-medium status-chip">
                                        <v-icon left x-small>mdi-check-circle</v-icon>
                                        Published
                                    </v-chip>
                                    <v-btn v-if="item.published" icon x-small class="ml-2" color="primary"
                                        @click.stop="openUrl(item.publishedUrl)">
                                        <v-icon small>mdi-open-in-new</v-icon>
                                    </v-btn>
                                    <v-chip v-else color="grey" text-color="white" small pill
                                        class="font-weight-medium status-chip">
                                        <v-icon left x-small>mdi-pencil</v-icon>
                                        Draft
                                    </v-chip>
                                    <v-menu v-if="item.published" offset-y>
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn icon x-small class="ml-2" color="primary" v-bind="attrs" v-on="on">
                                                <v-icon small>mdi-share</v-icon>
                                            </v-btn>
                                        </template>
                                        <v-list dense>
                                            <v-list-item @click.stop="shareToFacebook(item)">
                                                <v-list-item-icon>
                                                    <v-icon small color="indigo">mdi-facebook</v-icon>
                                                </v-list-item-icon>
                                                <v-list-item-title>Facebook</v-list-item-title>
                                            </v-list-item>
                                            <v-list-item @click.stop="shareToPinterest(item)">
                                                <v-list-item-icon>
                                                    <v-icon small color="red">mdi-pinterest</v-icon>
                                                </v-list-item-icon>
                                                <v-list-item-title>Pinterest</v-list-item-title>
                                            </v-list-item>
                                            <v-list-item @click.stop="shareToInstagram(item)">
                                                <v-list-item-icon>
                                                    <v-icon small color="purple">mdi-instagram</v-icon>
                                                </v-list-item-icon>
                                                <v-list-item-title>Instagram</v-list-item-title>
                                            </v-list-item>
                                        </v-list>
                                    </v-menu>
                                </div>
                            </template>

                            <template v-slot:item.listedOn="{ item }">
                                <span v-if="item.listedOn">{{ formatDate(item.listedOn) }}</span>
                                <span v-else>-</span>
                            </template>

                            <template v-slot:item.publishedOn="{ item }">
                                <span v-if="item.publishedOn">{{ formatDate(item.publishedOn) }}</span>
                                <span v-else>-</span>
                            </template>

                            <template v-slot:progress>
                                <v-progress-linear color="primary" indeterminate></v-progress-linear>
                            </template>
                        </v-data-table>
                    </v-card>

                    <v-card flat class="pagination-card my-4">
                        <v-card-text class="pa-4 d-flex align-center justify-space-between flex-wrap">
                            <div class="d-flex align-center pagination-info">
                                <v-btn-toggle v-model="itemsPerPage" mandatory class="rows-toggle elevation-0 mr-4"
                                    dense border>
                                    <v-btn v-for="size in [10, 25, 50]" :key="size" :value="size" small text
                                        class="rows-btn">
                                        {{ size }}
                                    </v-btn>
                                </v-btn-toggle>
                                <div class="caption grey--text text--darken-2 font-weight-medium">
                                    Showing {{ (page - 1) * itemsPerPage + 1 }}-{{ Math.min(page * itemsPerPage,
                                        totalListings) }} of {{
                                        totalListings }} records
                                </div>
                            </div>

                            <div class="d-flex align-center">
                                <v-btn icon small :disabled="page === 1" @click="page = Math.max(1, page - 1)"
                                    class="mr-2">
                                    <v-icon>mdi-chevron-left</v-icon>
                                </v-btn>

                                <div class="pagination-pages d-none d-sm-flex">
                                    <v-btn v-for="pageNum in paginationButtons" :key="pageNum"
                                        :outlined="pageNum !== page" :color="pageNum === page ? 'primary' : ''"
                                        :disabled="pageNum === '...'" small fab class="mx-1 pagination-btn"
                                        @click="page = pageNum">
                                        {{ pageNum }}
                                    </v-btn>
                                </div>

                                <v-btn icon small :disabled="page === totalPages || totalPages === 0"
                                    @click="page = Math.min(totalPages, page + 1)" class="ml-2">
                                    <v-icon>mdi-chevron-right</v-icon>
                                </v-btn>
                            </div>
                        </v-card-text>
                    </v-card>

                </v-col>

            </v-row>
        </div>


    </v-container>
</template>
<script>

import store from "@/store/store";
import axios from "axios";

export default {
    data() {
        return {
            ENDPOINT: process.env.VUE_APP_ENDPOINT,
            sku: "",
            searchQuery: '',
            listings: [],
            totalListings: 0,
            page: 1,
            itemsPerPage: 10,
            totalCount: 0,
            pendingCount: 0,
            publishedCount: 0,
            locationSavingId: null,
            storageLocationOptions: [
                { value: 'warehouse', label: 'Warehouse', icon: 'mdi-warehouse' },
                { value: 'office', label: 'Office', icon: 'mdi-office-building' },
            ],
            headers: [
                { text: 'Title', value: 'title' },
                { text: 'Loc', value: 'storageLocation', sortable: false, width: '72px' },
                { text: 'Price', value: 'listPrice' },
                { text: 'Image', value: 'images' },
                { text: 'Status', value: 'published' },
                { text: 'Listed On', value: 'listedOn' },
                { text: 'Published On', value: 'publishedOn' },
            ],
            loading: false,
            user: store.getters.user,
            totalPages: 1
        }
    },

    computed: {
        /**
         * Calculate pagination buttons to display
         * Shows first page, last page, current page, and pages around current page
         */
        paginationButtons() {
            if (this.totalPages <= 7) {
                // If we have 7 or fewer pages, show all page numbers
                return Array.from({ length: this.totalPages }, (_, i) => i + 1);
            }

            // Always show first page, last page, and current page
            // Plus 1-2 pages before and after current page
            const buttons = [1];

            // Calculate range around current page
            const rangeStart = Math.max(2, this.page - 1);
            const rangeEnd = Math.min(this.totalPages - 1, this.page + 1);

            // Add ellipsis if needed before range
            if (rangeStart > 2) {
                buttons.push('...');
            }

            // Add page numbers in the range
            for (let i = rangeStart; i <= rangeEnd; i++) {
                buttons.push(i);
            }

            // Add ellipsis if needed after range
            if (rangeEnd < this.totalPages - 1) {
                buttons.push('...');
            }

            // Add last page if not already included
            if (this.totalPages > 1) {
                buttons.push(this.totalPages);
            }

            return buttons;
        },
    },

    watch: {

        page() {
            this.fetchListings();
        },
        itemsPerPage() {
            this.fetchListings();
        },
        searchQuery: {
            handler() {
                this.page = 1; // Reset to the first page when a new search term is entered
                this.fetchListings();
            },
            immediate: false, // Don't run the handler right away
            deep: false, // Don't deep-watch the object
        },
    },
    async created() {
        // Fetch initial data on component creation
        this.fetchListings();
        this.fetchCounts();
    },
    methods: {
        getImageUrl(image) {
            if (!image) return '';
            if (image.startsWith('http://') || image.startsWith('https://')) return image;
            return `${this.ENDPOINT}/uploads/compressed/${image}`;
        },
        normalizedStorageLocation(item) {
            return item.storageLocation === 'office' ? 'office' : 'warehouse';
        },
        storageLocationShort(item) {
            return this.normalizedStorageLocation(item) === 'office' ? 'Off' : 'WH';
        },
        storageLocationIcon(item) {
            return this.normalizedStorageLocation(item) === 'office'
                ? 'mdi-office-building'
                : 'mdi-warehouse';
        },
        storageLocationChipClass(item) {
            return this.normalizedStorageLocation(item) === 'office'
                ? 'storage-loc-chip--office'
                : 'storage-loc-chip--warehouse';
        },
        async saveStorageLocation(item, storageLocation) {
            if (!storageLocation || storageLocation === this.normalizedStorageLocation(item)) {
                return;
            }
            this.locationSavingId = item._id;
            try {
                await axios.patch(`${this.ENDPOINT}/listings/${item._id}/storage-location`, {
                    storageLocation,
                });
                this.$set(item, 'storageLocation', storageLocation);
            } catch (error) {
                this.$swal({
                    title: 'Error',
                    text: 'Could not save location. Please try again.',
                    icon: 'error',
                });
            } finally {
                this.locationSavingId = null;
            }
        },
        /**
         * Fetch listing counts from the server
         */
        async fetchCounts() {
            try {
                const response = await axios.get(`${this.ENDPOINT}/listings-count`);
                const { totalCount, pendingCount, publishedCount } = response.data;
                this.totalCount = totalCount;
                this.pendingCount = pendingCount;
                this.publishedCount = publishedCount;
            } catch (error) {
                this.$emit('show-snackbar', {
                    text: 'Error fetching listing counts',
                    color: 'error'
                });
            }
        },

        /**
         * Open URL in new tab
         */
        openUrl(url) {
            if (!url) return;
            window.open(url, "_blank");
        },
        /**
         * Fetch listings from server with pagination and search
         */
        async fetchListings() {
            try {
                this.loading = true;
                const response = await axios.get(`${this.ENDPOINT}/listings`, {
                    params: {
                        page: this.page - 1,  // 0-indexed for the backend
                        limit: this.itemsPerPage,
                        search: this.searchQuery
                    },
                });

                const { listings, totalCount, totalPages } = response.data;
                this.listings = listings;
                this.totalListings = totalCount;
                this.totalPages = totalPages;
            } catch (error) {
                this.$emit('show-snackbar', {
                    text: 'Error fetching listings',
                    color: 'error'
                });
            } finally {
                this.loading = false;
            }
        },


        /**
         * Navigate to edit page for the entered SKU
         */
        openSku() {
            if (!this.sku.trim()) return;
            this.$router.replace(`/release/edit/${this.sku}`);
        },
        /**
         * Navigate to the appropriate edit page based on product type
         */
        rowClicked(item) {
            if (!item || !item.sku) return;

            // Navigate to standard release edit path for vinyl, cd, or cassette
            const standardTypes = ["vinyl", "cd", "cassette"];

            if (!item.productType || standardTypes.includes(item.productType)) {
                this.$router.push({ path: `/release/edit/${item.sku}` });
            } else {
                // For other product types, include the format as a query parameter
                this.$router.push({
                    path: `/releaseother/edit/${item.sku}`,
                    query: { format: item.productType }
                });
            }
        },
        formatDate(date) {
            if (!date) return '-';
            const d = new Date(date);
            const day = String(d.getDate()).padStart(2, '0');
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const year = d.getFullYear();
            return `${day}-${month}-${year}`;
        },
        /**
         * Build common share data from a listing item
         */
        buildShareData(item) {
            const url = item.publishedUrl || '';
            const title = item.title || 'Check this out';
            const price = item.listPrice ? `Price: ${item.listPrice}` : '';
            const image = (item.images && item.images.length)
                ? this.getImageUrl(item.images[0])
                : '';
            const text = [title, price, url].filter(Boolean).join(' \n');
            return { url, title, text, image };
        },
        /**
         * Fetch an image URL and return a File for Web Share API
         */
        async buildImageFile(imageUrl, suggestedName = 'image.jpg') {
            try {
                const res = await fetch(imageUrl, { mode: 'cors' });
                const blob = await res.blob();
                return new File([blob], suggestedName, { type: blob.type || 'image/jpeg' });
            } catch (e) {
                return null;
            }
        },
        /**
         * Download an image to the user's device
         */
        async downloadImage(imageUrl, suggestedName = 'image.jpg') {
            try {
                const res = await fetch(imageUrl, { mode: 'cors' });
                const blob = await res.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = suggestedName;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
            } catch (e) {
                // noop
            }
        },
        /**
         * Copy a string to clipboard (fallback helper)
         */
        async copyToClipboard(text) {
            try {
                await navigator.clipboard.writeText(text);
                this.$emit('show-snackbar', { text: 'Copied to clipboard', color: 'success' });
            } catch (e) {
                this.$emit('show-snackbar', { text: 'Copy failed', color: 'error' });
            }
        },
        /**
         * Share to Facebook using sharer URL
         */
        shareToFacebook(item) {
            if (!item) return;
            const { url } = this.buildShareData(item);
            if (!url) return;
            const message = `Buy at : ${url}`;
            const params = new URLSearchParams({ u: url, quote: message });
            const shareUrl = `https://www.facebook.com/sharer/sharer.php?${params.toString()}`;
            window.open(shareUrl, '_blank', 'noopener,noreferrer');
            // Also copy message so the user can paste into "What's on your mind?"
            this.copyToClipboard(message);
        },
        /**
         * Share to Pinterest with media and description
         */
        shareToPinterest(item) {
            if (!item) return;
            const { url, title, image } = this.buildShareData(item);
            if (!url) return;
            const params = new URLSearchParams({
                url,
                media: image || url,
                description: title
            });
            const shareUrl = `https://www.pinterest.com/pin/create/button/?${params.toString()}`;
            window.open(shareUrl, '_blank', 'noopener,noreferrer');
        },
        /**
         * Instagram does not support direct web URL shares. Use Web Share API where available,
         * otherwise copy caption and prompt user to post manually in Instagram app.
         */
        /**
 * Share to Instagram
 * Desktop: downloads image, copies caption, opens Instagram Create page.
 * Mobile (if Web Share API available): tries native share with file/text.
 */
 async shareToInstagram(item) {
  if (!item) return;

  const { title, url, image } = this.buildShareData(item);

  // Mobile detection
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );

  // REQUIRED desktop caption format
  const desktopCaption =
    `Buy at : ${url || ""}\n#vinylrecord #audiocassette #audiocd #vinyl`.trim();

  // Try native share on mobile first
  if (isMobile && navigator.share) {
    try {
      // Attempt to attach image if supported
      if (image && navigator.canShare && typeof navigator.canShare === "function") {
        const file = await this.buildImageFile(image, `${item.sku || "listing"}.jpg`);
        if (file && navigator.canShare({ files: [file] })) {
          await navigator.share({ title: title || "CalcuttaRecords", text: desktopCaption, files: [file] });
          return;
        }
      }
      // Fallback: share link + caption
      await navigator.share({
        title: title || "CalcuttaRecords",
        text: desktopCaption,
        url: url || undefined
      });
      return;
    } catch (_) {
      // Fall through to desktop if share fails
    }
  }

  // Desktop (or mobile without share support): copy exact caption, download image, open IG Create
  await this.shareToInstagramDesktop({
    caption: desktopCaption,
    imageUrl: image,
    filename: `${item.sku || "listing"}.jpg`,
  });
},

/**
 * Desktop Instagram flow:
 * 1) Copy caption to clipboard
 * 2) Download image to user's device (if available)
 * 3) Open Instagram Create page in a new tab
 */
async shareToInstagramDesktop({ caption, imageUrl, filename = "image.jpg" }) {
  try {
    // 1) Copy caption first so it's ready to paste
    if (caption) {
      try {
        await navigator.clipboard.writeText(caption);
        this.$emit("show-snackbar", { text: "Caption copied to clipboard.", color: "success" });
      } catch {
        this.$emit("show-snackbar", { text: "Could not copy caption. Please copy manually.", color: "warning" });
      }
    }

    // 2) Download image (so user can upload in Instagram)
    if (imageUrl) {
      try {
        await this.downloadImage(imageUrl, filename);
        this.$emit("show-snackbar", { text: "Image downloaded.", color: "success" });
      } catch {
        this.$emit("show-snackbar", { text: "Image download failed. Opening image in new tab.", color: "warning" });
        window.open(imageUrl, "_blank", "noopener,noreferrer");
      }
    }

    // 3) Open Instagram Create page
    const win = window.open("https://www.instagram.com/create/select/", "_blank", "noopener,noreferrer");
    if (!win) {
      this.$emit("show-snackbar", {
        text: "Pop-up blocked. Please allow pop-ups for this site and try again.",
        color: "error",
      });
    } else {
      this.$emit("show-snackbar", {
        text: "Instagram Create opened. Upload the image and paste the caption.",
        color: "info",
      });
    }
  } catch (e) {
    this.$emit("show-snackbar", {
      text: "Something went wrong while preparing the Instagram post.",
      color: "error",
    });
  }
},

    }
}


</script>
<style scoped>
.main-container {
    max-width: 95vw;
    padding-bottom: 2rem;
}

.header-card {
    background-color: #ffffff;
    border-radius: 8px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.action-card {
    background-color: #ffffff;
    border-radius: 8px;
    border: 1px solid rgba(0, 0, 0, 0.05);
}

.action-btn {
    text-transform: none;
    letter-spacing: 0;
    font-weight: 500;
    border-radius: 4px;
    height: 40px;
}

.data-table-card {
    border-radius: 8px;
    border: 1px solid rgba(0, 0, 0, 0.05);
    overflow: hidden;
}

.storage-loc-chip {
    height: 22px !important;
    font-size: 11px !important;
    font-weight: 600;
    letter-spacing: 0.02em;
    cursor: pointer;
    user-select: none;
}

.storage-loc-chip--warehouse {
    background: #eceff1 !important;
    color: #546e7a !important;
}

.storage-loc-chip--office {
    background: #ede7f6 !important;
    color: #5e35b1 !important;
}

.storage-loc-menu {
    min-width: 148px;
}

.storage-loc-menu .v-list-item {
    min-height: 36px;
}

.table-rounded {
    border-radius: 0 0 8px 8px;
}

.search-query {
    min-width: 20rem;
}

.search-bar {
    max-width: 300px;
}

.items-per-page-select {
    width: 70px;
}

.pagination-card {
    border: 1px solid rgba(0, 0, 0, 0.05);
    border-radius: 8px;
    background-color: #ffffff;
}

.pagination-info {
    min-width: 200px;
}

.rows-toggle {
    border-radius: 4px;
    overflow: hidden;
}

.rows-toggle ::v-deep .v-btn {
    min-width: 36px !important;
    height: 28px !important;
    font-size: 0.75rem;
    text-transform: none;
    letter-spacing: 0;
    font-weight: 500;
}

.pagination-btn {
    width: 32px !important;
    height: 32px !important;
    min-width: 32px !important;
    font-size: 0.75rem;
    font-weight: 500;
}

.product-title {
    color: #1976d2;
    text-decoration: none;
    font-weight: 500;
}

.product-title:hover {
    cursor: pointer;
    text-decoration: underline;
}

.top-heading {
    font-size: 1.8rem;
    font-weight: 700;
    letter-spacing: 0.5px;
    margin-bottom: 4px;
}

.stat-card {
    height: 100%;
    transition: transform 0.2s;
    border-radius: 8px;
    overflow: hidden;
}

.stat-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.1);
}

.gradient-primary {
    background: linear-gradient(45deg, #1976d2, #64b5f6);
}

.gradient-amber {
    background: linear-gradient(45deg, #ff8f00, #ffca28);
}

.gradient-success {
    background: linear-gradient(45deg, #2e7d32, #66bb6a);
}

.stat-value {
    font-size: 2rem;
    font-weight: 700;
    line-height: 1.2;
}

.stat-label {
    font-size: 0.9rem;
    font-weight: 500;
    margin-top: 4px;
}

.image-container {
    position: relative;
    display: inline-block;
}

.image-overlay {
    border-radius: 50%;
}

.on-hover {
    transition: all 0.2s;
}

.status-chip {
    font-size: 0.75rem;
    height: 24px;
}

.status-container {
    display: flex;
    align-items: center;
}

.price-chip {
    font-weight: 500;
}
</style>